import type { QuickMatchState, TeamSide, QuickMatchHistoryEntry } from './quickMatchTypes.ts';

export type QuickMatchAction =
  | { type: 'INIT'; homeTeamName: string; awayTeamName: string; servingTeam: TeamSide; pointsToWin: number }
  | { type: 'ADD_POINT'; side: TeamSide }
  | { type: 'UNDO' }
  | { type: 'SWAP_SIDES' }
  | { type: 'START_NEW_SET'; pointsToWin: number };

export function createInitialState(params: {
  homeTeamName: string;
  awayTeamName: string;
  servingTeam: TeamSide;
  pointsToWin: number;
}): QuickMatchState {
  return {
    homeTeamName: params.homeTeamName,
    awayTeamName: params.awayTeamName,
    homePoints: 0,
    awayPoints: 0,
    setsWon: { home: 0, away: 0 },
    completedSets: [],
    currentSet: 1,
    servingTeam: params.servingTeam,
    pointsToWin: params.pointsToWin,
    swapped: false,
    history: [],
    phase: 'PLAYING',
  };
}

function isSetOver(home: number, away: number, pointsToWin: number): boolean {
  return Math.max(home, away) >= pointsToWin && Math.abs(home - away) >= 2;
}

export function quickMatchReducer(state: QuickMatchState, action: QuickMatchAction): QuickMatchState {
  switch (action.type) {
    case 'INIT':
      return createInitialState({
        homeTeamName: action.homeTeamName,
        awayTeamName: action.awayTeamName,
        servingTeam: action.servingTeam,
        pointsToWin: action.pointsToWin,
      });

    case 'ADD_POINT': {
      const snapshot: QuickMatchHistoryEntry = {
        homePoints: state.homePoints,
        awayPoints: state.awayPoints,
        servingTeam: state.servingTeam,
      };

      const homePoints = action.side === 'HOME' ? state.homePoints + 1 : state.homePoints;
      const awayPoints = action.side === 'AWAY' ? state.awayPoints + 1 : state.awayPoints;

      // Side-out: chi segna prende il servizio
      const servingTeam: TeamSide = action.side;

      if (isSetOver(homePoints, awayPoints, state.pointsToWin)) {
        const winner: TeamSide = homePoints > awayPoints ? 'HOME' : 'AWAY';
        return {
          ...state,
          homePoints,
          awayPoints,
          servingTeam,
          setsWon: {
            home: state.setsWon.home + (winner === 'HOME' ? 1 : 0),
            away: state.setsWon.away + (winner === 'AWAY' ? 1 : 0),
          },
          completedSets: [
            ...state.completedSets,
            { setNumber: state.currentSet, homePoints, awayPoints },
          ],
          history: [...state.history, snapshot],
          phase: 'SET_OVER',
        };
      }

      return {
        ...state,
        homePoints,
        awayPoints,
        servingTeam,
        history: [...state.history, snapshot],
      };
    }

    case 'UNDO': {
      if (state.history.length === 0) return state;
      const history = [...state.history];
      const prev = history.pop()!;

      const wasSetOver = state.phase === 'SET_OVER';
      const completedSets = wasSetOver ? state.completedSets.slice(0, -1) : state.completedSets;
      const setsWon = wasSetOver
        ? {
            home: state.setsWon.home - (state.homePoints > state.awayPoints ? 1 : 0),
            away: state.setsWon.away - (state.awayPoints > state.homePoints ? 1 : 0),
          }
        : state.setsWon;

      return {
        ...state,
        homePoints: prev.homePoints,
        awayPoints: prev.awayPoints,
        servingTeam: prev.servingTeam,
        setsWon,
        completedSets,
        history,
        phase: 'PLAYING',
      };
    }

    case 'SWAP_SIDES':
      return { ...state, swapped: !state.swapped };

    case 'START_NEW_SET':
      return {
        ...state,
        homePoints: 0,
        awayPoints: 0,
        history: [],
        currentSet: state.currentSet + 1,
        pointsToWin: action.pointsToWin,
        phase: 'PLAYING',
      };

    default:
      return state;
  }
}
